import { createAction } from '@reduxjs/toolkit';

import type { AccountsTypes, AppState } from '@modules';
import api from '@modules/api';
import { FiatCurrencyEnum } from '@modules/global/types';
import { createActions, createAsyncActions } from '@modules/helpers';

import { AccountCreateStatus } from './types';

export const actions = createActions('accounts', {
  clearVerifyData: name => createAction<void>(name),

  setAccountCreated: name => createAction<AccountCreateStatus>(name),
});

export const asyncActions = createAsyncActions('accounts', {
  getAccounts: (_: void, thunkApi) =>
    api.get<AccountsTypes.AccountsResponse[]>(thunkApi, '/accounts'),

  getAccountsPolling: (_: void, thunkApi) =>
    api.get<AccountsTypes.AccountsResponse[]>(thunkApi, '/accounts'),

  createIntention: (_: void, thunkApi) =>
    api.post<AccountsTypes.AccountIntention>(thunkApi, '/accounts/intention'),

  createAccount: (_: void, thunkApi) =>
    api.post<AccountsTypes.AccountsResponse>(thunkApi, '/accounts'),

  accountBuy: (data: AccountsTypes.AccountsBuyParams, thunkApi) =>
    api.post<AccountsTypes.AccountsBuyResponse>(thunkApi, '/accounts/buy', data),

  accountSell: (data: AccountsTypes.AccountsSellParams, thunkApi) =>
    api.post<AccountsTypes.AccountsSellResponse>(thunkApi, '/accounts/sell', data),

  accountWithdraw: (data: AccountsTypes.AccountsWithdrawParams, thunkApi) =>
    api.post<AccountsTypes.AccountsWithdrawResponse>(thunkApi, '/accounts/withdraw', data),

  accountWithdrawVerify: (data: AccountsTypes.AccountsWithdrawVerifyParams, thunkApi) => {
    const url = `/accounts/withdraw/${data.type}-verify`;
    return api.post<AccountsTypes.AccountsWithdrawVerifyResponse>(thunkApi, url, data);
  },

  accountWithdrawVerifyResend: (
    data: AccountsTypes.AccountsWithdrawVerifyResendParams,
    thunkApi
  ) => {
    const url = '/accounts/withdraw/email-verify/resend';
    return api.post<AccountsTypes.AccountsWithdrawVerifyResendResponse>(thunkApi, url, data);
  },

  getAccountRecentTransactions: (_: void, thunkApi) =>
    api.get<AccountsTypes.AccountsRecentTransactionsResponse>(
      thunkApi,
      '/accounts/recent-transaction-accounts'
    ),

  getAccountEstimateFee: (data: AccountsTypes.AccountsEstimateFeeParams, thunkApi) =>
    api.get<AccountsTypes.AccountsEstimateFeeResponse>(thunkApi, '/accounts/estimate-fee', {
      params: data,
    }),

  getAccountLimits: (params: AccountsTypes.AccountsLimitsParams, thunkApi) =>
    api.get<AccountsTypes.AccountsLimitsResponse>(thunkApi, '/accounts/limits', { params }),

  getAccountLimitsPolling: (params: AccountsTypes.AccountsLimitsParams, thunkApi) =>
    api.get<AccountsTypes.AccountsLimitsResponse>(thunkApi, '/accounts/limits', { params }),

  getAccountSellRate: (data: AccountsTypes.AccountsSellRateParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const { fiat } = state.global.currencies;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const is_total = fiat.includes(data.to as FiatCurrencyEnum);
    const params = { ...data, is_total };
    return api.get<AccountsTypes.AccountsSellRateResponse>(thunkApi, '/accounts/sell-rate', {
      params,
    });
  },

  getAccountBuyRate: (data: AccountsTypes.AccountsBuyRateParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const { fiat } = state.global.currencies;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const is_total = fiat.includes(data.from as FiatCurrencyEnum);
    const params = { ...data, is_total };
    return api.get<AccountsTypes.AccountsBuyRateResponse>(thunkApi, '/accounts/buy-rate', {
      params,
    });
  },
});

export default { ...actions, ...asyncActions };
